/* Guard against browser extensions that mutate React-managed DOM.
 *
 * Click-to-call dialers, Google Translate, and similar extensions wrap or
 * reparent text nodes that React owns (e.g. phone numbers in the PRO drawer's
 * Invoicing / Addl Info tabs). On its next commit React calls
 * removeChild/insertBefore against the original parent, the DOM throws
 * NotFoundError, and with no recovery path the whole tree unmounts to a white
 * page — the "every save white-screens" crash of Aug 2026.
 *
 * Native behavior is preserved whenever the DOM is untouched; the patch only
 * kicks in when a node has been reparented by something outside React:
 * removeChild returns the node unremoved, insertBefore falls back to append
 * (slightly misplaced content beats none, and React re-syncs on the next
 * render of that subtree). Community-standard workaround from
 * facebook/react#11538. Must be imported before the first React render.
 */

if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChildGuarded<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      console.warn(
        'domExtensionGuard: skipped removeChild of a node something outside React reparented',
        child,
      );
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBeforeGuarded<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.warn(
        'domExtensionGuard: insertBefore reference node was reparented; appending instead',
        referenceNode,
      );
      return this.appendChild(newNode) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

export {};
